// src/pages/ArtHouse/ArtHouseFeedPage.jsx
// Primary social feed (Instagram-style) for ArtHouse.
// IMPORTANT: This file matches the working Gallery page API shape:
// fetchArtworks(...) -> { results: [...], pagination: { hasMore: boolean } }

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  UserCircle,
  Bookmark,
  Flag,
  Share2,
  Link as LinkIcon,
  Upload,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { fetchArtworks } from "../../services/arthouseApi";
import {
  buildOptimisticLikeState,
  readCanonicalLikeState,
  reconcileLikeStateFromResponse,
} from "../../utils/likeInteraction";

// Use your extracted component
import ArtHouseFeedPostCard from "../../components/ArtHouse/ArtHouseFeedPostCard";

// -------------------------
// Helpers
// -------------------------
function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function getArtistIdFromPost(post) {
  return (
    post?.artist?.id ??
    post?.artist?.artist_id ??
    post?.artist?.user_id ??
    post?.artist_id ??
    post?.user_id ??
    null
  );
}

function pickImageUrl(a) {
  return (
    a?.imageUrl ??
    a?.image_url ??
    a?.image ??
    a?.url ??
    a?.image_path ??
    a?.imagePath ??
    a?.artwork_url ??
    ""
  );
}

function normalizeArtwork(a) {
  const id = getArtworkId(a);
  const imageUrl = pickImageUrl(a);

  const title = a?.title ?? a?.name ?? "";
  const caption = a?.caption ?? a?.description ?? a?.bio ?? "";
  const createdAt = a?.createdAt ?? a?.created_at ?? null;

  const tagsRaw =
    a?.tags ??
    a?.tagList ??
    a?.art_tags ??
    a?.artTags ??
    a?.tag_names ??
    [];

  const tags = safeArr(tagsRaw)
    .map((t) => (typeof t === "string" ? t : t?.label ?? t?.name ?? ""))
    .filter(Boolean)
    .slice(0, 8);

  const creator = a?.creator ?? a?.user ?? a?.author ?? a?.artist ?? {};
  const creatorName =
    creator?.displayName ??
    creator?.display_name ??
    creator?.name ??
    a?.creator_name ??
    a?.artist_name ??
    a?.user_name ??
    a?.username ??
    "";
  const creatorHandle =
    creator?.handle ??
    creator?.username ??
    creator?.user_name ??
    a?.creator_username ??
    a?.username ??
    "";
  const creatorAvatar =
    creator?.avatarUrl ??
    creator?.avatar_url ??
    creator?.avatar ??
    a?.creator_avatar ??
    a?.artist_avatar ??
    "";

  const stats = a?.stats ?? {};
  const likeCount = Number(a?.likeCount ?? stats?.likes ?? a?.likes ?? 0) || 0;
  const commentCount =
    Number(a?.commentCount ?? stats?.comments ?? a?.comments ?? 0) || 0;
  const saveCount = Number(a?.saveCount ?? stats?.saves ?? a?.saves ?? 0) || 0;

  const liked = Boolean(a?.liked ?? a?.isLiked ?? false);
  const saved = Boolean(a?.saved ?? a?.isSaved ?? false);

  const previewComments =
    a?.previewComments ?? a?.commentsPreview ?? a?.topComments ?? [];

  return {
    ...a,
    id,
    imageUrl,

    title,
    caption,
    description: caption,
    createdAt,

    tags,
    stats: {
      likes: likeCount,
      comments: commentCount,
      saves: saveCount,
    },

    viewerState: {
      liked,
      saved,
    },

    artist: {
      id:
        creator?.id ??
        creator?.user_id ??
        creator?.artist_id ??
        a?.user_id ??
        a?.artist_id ??
        null,

      ...(creatorName && creatorName !== "Creator"
        ? { display_name: creatorName }
        : {}),

      ...(creatorHandle
        ? { username: String(creatorHandle).replace(/^@/, "") }
        : creator?.username
        ? { username: String(creator.username).replace(/^@/, "") }
        : a?.creator_username
        ? { username: String(a.creator_username).replace(/^@/, "") }
        : {}),

      ...(creatorAvatar ? { avatar_url: creatorAvatar } : {}),
    },

    previewComments: safeArr(previewComments).slice(0, 2),
  };
}

// -------------------------
// Main Page
// -------------------------
export default function ArtHouseFeedPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const API_BASE = process.env.REACT_APP_API_BASE || "/api";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({ hasMore: false });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [error, setError] = useState("");

  const sentinelRef = useRef(null);
  const feedScrollRef = useRef(null); // scroll container root for IO

  // Local optimistic states (likes/saves) keyed by artwork id
  const [optimistic, setOptimistic] = useState(() => ({}));
  const [likeBusyById, setLikeBusyById] = useState(() => ({}));

  // Menu state
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // feed mode tabs (For You / Following) — inert for now
  const feedMode = "foryou"; // "foryou" | "following"

  // Preserve hub state + mark origin so CreatorTopBar can show Back
  const hubState = useMemo(() => {
    const incoming = location.state || {};
    return {
      ...incoming,
      hubSection: incoming.hubSection || "arthouse",
      from: incoming.from || "arthouse-feed",
    };
  }, [location.state]);

  // Profile button UI state
  const [openingProfile, setOpeningProfile] = useState(false);

  async function goToMyArtistProfile() {
    if (!user) return nav("/login");
    setOpeningProfile(true);
    try {
      nav("/dashboard/profile", { state: hubState });
    } finally {
      setOpeningProfile(false);
    }
  }

  const myProfileButtonLabel = openingProfile ? "Opening…" : "My Art Page";

  const mergedItems = useMemo(() => {
    return items.map((it) => {
      const o = optimistic[it.id];
      if (!o) return it;
      return {
        ...it,
        stats: {
          ...it.stats,
          likes: typeof o.likes === "number" ? o.likes : it.stats.likes,
          saves: typeof o.saves === "number" ? o.saves : it.stats.saves,
        },
        viewerState: {
          ...(it.viewerState || {}),
          liked: typeof o.liked === "boolean" ? o.liked : it.viewerState?.liked,
          saved: typeof o.saved === "boolean" ? o.saved : it.viewerState?.saved,
        },
      };
    });
  }, [items, optimistic]);

  const hasMore = Boolean(pagination?.hasMore);

  const loadPage = useCallback(
    async (nextPage, mode = feedMode) => {
      if (loading) return;
      if (!hasMore && nextPage !== 1) return;

      setLoading(true);
      setError("");

      try {
        const sortToUse = mode === "following" ? "following" : "new";

        const data = await fetchArtworks({
          sort: sortToUse,
          page: nextPage,
          limit: 8,
        });

        const rawList = Array.isArray(data?.results) ? data.results : [];
        const normalized = rawList.map(normalizeArtwork).filter((x) => x.id);

        setItems((prev) => {
          if (nextPage === 1) return normalized;

          const seen = new Set(prev.map((p) => p.id));
          const deduped = normalized.filter((n) => !seen.has(n.id));
          return [...prev, ...deduped];
        });

        setPagination(data?.pagination || { hasMore: normalized.length > 0 });
        setPage(nextPage);
      } catch (e) {
        const msg = e?.message || "Failed to load feed.";
        const looksLikeUnsupportedFollowing =
          String(msg).toLowerCase().includes("following") ||
          String(msg).toLowerCase().includes("sort") ||
          String(msg).toLowerCase().includes("invalid");

        if (mode === "following" && looksLikeUnsupportedFollowing) {
          try {
            const fallback = await fetchArtworks({
              sort: "new",
              page: nextPage,
              limit: 8,
            });

            const rawList2 = Array.isArray(fallback?.results)
              ? fallback.results
              : [];
            const normalized2 = rawList2
              .map(normalizeArtwork)
              .filter((x) => x.id);

            setItems((prev) => {
              if (nextPage === 1) return normalized2;
              const seen = new Set(prev.map((p) => p.id));
              const deduped = normalized2.filter((n) => !seen.has(n.id));
              return [...prev, ...deduped];
            });

            setPagination(
              fallback?.pagination || { hasMore: normalized2.length > 0 }
            );
            setPage(nextPage);
          } catch (e2) {
            setError(e2?.message || "Failed to load feed.");
          }
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [feedMode, hasMore, loading]
  );

  useEffect(() => {
    loadPage(1, feedMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !items.length) return;
    let alive = true;

    const payloadItems = items
      .map((it) => Number(it?.id))
      .filter((id) => Number.isFinite(id))
      .map((id) => ({ target_type: "artwork", target_id: id }));

    if (!payloadItems.length) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/likes/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ items: payloadItems }),
        });
        if (!res.ok) return;
        const rows = await res.json();
        if (!alive || !Array.isArray(rows)) return;

        setOptimistic((prev) => {
          const next = { ...prev };
          for (const row of rows) {
            const id = Number(row?.target_id);
            if (!Number.isFinite(id)) continue;
            next[id] = {
              ...(next[id] || {}),
              ...(() => {
                const canonical = readCanonicalLikeState(row);
                return {
                  liked: canonical.liked_by_viewer,
                  likes: canonical.like_count,
                };
              })(),
            };
          }
          return next;
        });
      } catch {
        // keep feed usable if like-status prefetch fails
      }
    })();

    return () => {
      alive = false;
    };
  }, [API_BASE, items, user]);

  // Infinite scroll observer (root = internal scroll container)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (loading) return;
        if (!hasMore) return;
        loadPage(page + 1, feedMode);
      },
      {
        root: feedScrollRef.current || null,
        rootMargin: "900px 0px",
        threshold: 0,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadPage, loading, page, feedMode]);

  // Persisted artwork likes with safe optimistic rollback.
  const toggleLike = useCallback(
    async (id) => {
      if (!user) {
        nav("/login");
        return;
      }
      if (likeBusyById[id]) return;

      const item = items.find((x) => x.id === id);
      const cur = optimistic[id] || {};
      const optimisticState = buildOptimisticLikeState({
        liked_by_viewer:
          typeof cur.liked === "boolean" ? cur.liked : Boolean(item?.viewerState?.liked),
        like_count:
          typeof cur.likes === "number" ? cur.likes : Number(item?.stats?.likes ?? 0) || 0,
      });

      setLikeBusyById((prev) => ({ ...prev, [id]: true }));
      setOptimistic((prev) => ({
        ...prev,
        [id]: {
          ...(prev[id] || {}),
          liked: optimisticState.optimisticLiked,
          likes: optimisticState.optimisticCount,
        },
      }));

      try {
        const res = await fetch(`${API_BASE}/likes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ target_type: "artwork", target_id: Number(id) }),
        });
        if (!res.ok) throw new Error("Failed to toggle like.");
        const json = await res.json();
        const canonical = reconcileLikeStateFromResponse(json, optimisticState);

        setOptimistic((prev) => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            liked: canonical.liked_by_viewer,
            likes: canonical.like_count,
          },
        }));
      } catch {
        setOptimistic((prev) => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            liked: optimisticState.prevLiked,
            likes: optimisticState.prevCount,
          },
        }));
      } finally {
        setLikeBusyById((prev) => ({ ...prev, [id]: false }));
      }
    },
    [API_BASE, items, likeBusyById, nav, optimistic, user]
  );

  const toggleSave = useCallback(
    (id) => {
      setOptimistic((prev) => {
        const cur = prev[id] || {};
        const item = items.find((x) => x.id === id);

        const baseSaved =
          typeof cur.saved === "boolean" ? cur.saved : item?.viewerState?.saved;
        const baseSaves =
          typeof cur.saves === "number" ? cur.saves : item?.stats?.saves ?? 0;

        const nextSaved = !Boolean(baseSaved);
        const nextSaves = Math.max(0, baseSaves + (nextSaved ? 1 : -1));

        return {
          ...prev,
          [id]: {
            ...cur,
            saved: nextSaved,
            saves: nextSaves,
          },
        };
      });
    },
    [items]
  );

  const openPost = useCallback(
    (id) => {
      nav(`/arthouse/art/${id}`, { state: hubState });
    },
    [nav, hubState]
  );

  const openArtistProfile = useCallback(
    ({ artistId, post }) => {
      const resolvedId = artistId ?? getArtistIdFromPost(post);
      if (!resolvedId) return;
      nav(`/arthouse/artist/${resolvedId}`, { state: hubState });
    },
    [nav, hubState]
  );

  const openUpload = useCallback(() => {
    if (!user) {
      nav("/login");
      return;
    }

    nav("/dashboard/art/new", {
      state: { backgroundLocation: location, ...hubState },
    });
  }, [hubState, location, nav, user]);

  const openExplore = useCallback(() => {
    nav("/arthouse/gallery", { state: hubState });
  }, [nav, hubState]);

  const openClubs = useCallback(() => {
    nav("/clubs", { state: hubState });
  }, [nav, hubState]);

  const toggleMenu = useCallback((id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  }, []);

  const closeMenu = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMenu();
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenuId, closeMenu]);

  const handleMenuAction = useCallback(
    async (action, post) => {
      closeMenu();

      switch (action) {
        case "save":
          toggleSave(post.id);
          break;
        case "copy-link":
          try {
            const url = `${window.location.origin}/arthouse/art/${post.id}`;
            await navigator?.clipboard?.writeText?.(url);
          } catch (err) {
            console.error("Failed to copy link", err);
          }
          break;
        case "share":
          // Trigger the existing share functionality
          try {
            const url = `${window.location.origin}/arthouse/art/${post.id}`;
            if (navigator?.share) {
              await navigator.share({
                title: post.title || "ArtHouse",
                url,
              });
            } else if (navigator?.clipboard?.writeText) {
              await navigator.clipboard.writeText(url);
            }
          } catch (err) {
            console.error("Failed to share", err);
          }
          break;
        case "report":
          // Placeholder for report functionality
          console.log("Report post:", post.id);
          break;
        default:
          break;
      }
    },
    [closeMenu, toggleSave]
  );

  return (
    <div className="w-full h-[100svh]">
      <div className="mx-auto w-full max-w-[760px] px-3 sm:px-4 pb-12 h-full flex flex-col min-h-0">
        <FeedTopBar
          feedMode={feedMode}
          onSearch={openExplore}
          onClubs={openClubs}
          onUpload={openUpload}
          onMyArt={goToMyArtistProfile}
          myArtLabel={myProfileButtonLabel}
          myArtDisabled={openingProfile}
        />

        {error ? (
          <div className="mt-5 rounded-2xl border border-rose-500/25 bg-rose-500/10 p-4 text-rose-100">
            <div className="text-sm font-semibold">Couldn’t load ArtHouse.</div>
            <div className="mt-1 text-sm text-rose-100/80">{error}</div>
            <button
              type="button"
              onClick={() => loadPage(1, feedMode)}
              className="mt-3 inline-flex h-10 items-center rounded-full border border-white/15 bg-white/5 px-4 text-sm font-semibold text-white/90 hover:bg-white/10"
            >
              Retry
            </button>
          </div>
        ) : null}

        {initialLoading ? (
          <div className="mt-5 space-y-4 sm:mt-6 sm:space-y-5">
            <PostSkeleton />
            <PostSkeleton />
          </div>
        ) : (
          // Invisible internal scroll container (no bg/border/padding added)
          <div className="flex-1 min-h-0 rounded-3xl overflow-hidden">
  <div
    ref={feedScrollRef}
    className="h-full overflow-y-auto scrollbar-hide"
  >
    <div className="mt-5 space-y-4 sm:mt-6 sm:space-y-6">
      {mergedItems.map((post) => (
        <div key={post.id} className="relative">
          <ArtHouseFeedPostCard
            post={post}
            onOpen={() => openPost(post.id)}
            onOpenArtist={({ artistId } = {}) =>
              openArtistProfile({ artistId, post })
            }
            onLike={() => toggleLike(post.id)}
            onSave={() => toggleSave(post.id)}
            onComment={() => openPost(post.id)}
            onShare={async ({ url }) => {
              try {
                await navigator?.clipboard?.writeText?.(url);
                return true;
              } catch {
                return false;
              }
            }}
            onMore={() => toggleMenu(post.id)}
          />
          {openMenuId === post.id && (
            <PostMenu
              ref={menuRef}
              post={post}
              onAction={(action) => handleMenuAction(action, post)}
            />
          )}
        </div>
      ))}

      <div ref={sentinelRef} className="h-1 w-full" />

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="rounded-full border border-white/12 bg-white/[0.035] px-4 py-2 text-sm text-white/70">
            Loading more…
          </div>
        </div>
      ) : null}

      {!loading && mergedItems.length === 0 ? (
        <EmptyState mode={feedMode} onExplore={openExplore} />
      ) : null}

      {!hasMore && mergedItems.length > 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-white/55">You’re all caught up.</div>
        </div>
      ) : null}
    </div>
  </div>
</div>

        )}
      </div>
    </div>
  );
}

// -------------------------
// Top bar (dark glass) + Tabs + Actions (no glows)
// -------------------------
function FeedTopBar({
  feedMode,
  onSearch,
  onClubs,
  onUpload,
  onMyArt,
  myArtLabel,
  myArtDisabled,
}) {
  const pill = `
    h-10 sm:h-11
    px-3 sm:px-4
    rounded-full
    border border-white/10
    bg-black/25
    backdrop-blur-xl
    transition
    hover:bg-black/30 hover:border-white/15
    text-[10px] sm:text-[11px]
    tracking-[0.18em]
    uppercase
    text-white/85
    flex items-center gap-2
    focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20
  `;

  const circle = `
    h-10 w-10 sm:h-11 sm:w-11
    rounded-full
    border border-white/10
    bg-black/25
    backdrop-blur-xl
    transition
    hover:bg-black/30 hover:border-white/15
    text-white/85
    inline-flex items-center justify-center
    focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20
  `;

  return (
    <div
      className="sticky top-0 z-30 -mx-3 sm:-mx-4 px-3 sm:px-4"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
    >
      {/* Dark glass curtain for readability (no glow) */}
      <div />

      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FeedModeTabs value={feedMode} />
            {/* Better ArtHouse label (clean + premium, not loud) */}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onMyArt}
              disabled={myArtDisabled}
              className={`${pill} disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label="Open my art page"
            >
              <UserCircle className="h-[18px] w-[18px] sm:h-5 sm:w-5 text-white/70" />
              <span className="hidden sm:inline">
                {myArtLabel || "My Art Page"}
              </span>
              <span className="sm:hidden">My Page</span>
            </button>

            <button
              type="button"
              onClick={onUpload}
              className={pill}
              aria-label="Upload artwork"
            >
              <Upload className="h-[18px] w-[18px] sm:h-5 sm:w-5 text-white/70" />
              <span className="sm:inline">Upload</span>
            </button>

            <button
              type="button"
              onClick={onClubs}
              className={pill}
              aria-label="Go to clubs"
            >
              <span className="sm:inline">Clubs</span>
            </button>

            <button
              type="button"
              onClick={onSearch}
              className={circle}
              aria-label="Explore"
            >
              <Search className="h-[17px] w-[17px] sm:h-[18px] sm:w-[18px] text-white/70" />
            </button>
          </div>
        </div>

        {/* Small, calm hint row (no glow, no extra color) */}
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[12px] text-white/50">
            {feedMode === "foryou" ? (
              <span></span>
            ) : (
              <span>
                Following <span className="text-white/25">•</span> only your
                artists
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedModeTabs({ value }) {
  const shell = `
    relative inline-flex items-center
    rounded-full
    border border-white/10
    bg-black/25
    backdrop-blur-xl
    p-1
    select-none
  `;

  const tab = (active) => `
    relative z-10
    h-10 sm:h-11
    px-4 sm:px-5
    rounded-full
    text-[11px] sm:text-[12px]
    font-semibold
    transition
    focus:outline-none
    ${active ? "text-white" : "text-white/45"}
  `;

  return (
    <div className="pointer-events-none" aria-label="Feed mode (coming soon)">
      <div className={shell} role="tablist">
        <div
          className={`
            absolute top-1 bottom-1
            w-1/2 rounded-full
            border border-white/10
            bg-white/[0.06]
            shadow-[0_10px_40px_rgba(0,0,0,0.45)]
            transition-transform duration-300
            ${value === "following" ? "translate-x-full" : "translate-x-0"}
          `}
          aria-hidden="true"
        />

        <button
          type="button"
          className={tab(value === "foryou")}
          role="tab"
          aria-selected={value === "foryou"}
          disabled
        >
          For You
        </button>

        <button
          type="button"
          className={tab(value === "following")}
          role="tab"
          aria-selected={value === "following"}
          disabled
        >
          Following
        </button>
      </div>
    </div>
  );
}

// -------------------------
// Empty + Skeleton
// -------------------------
function EmptyState({ mode, onExplore }) {
  const isFollowing = mode === "following";

  return (
    <div className="mt-8 rounded-3xl border border-white/10 bg-black/25 backdrop-blur-xl p-8 text-center shadow-[0_22px_110px_rgba(0,0,0,0.55)]">
      <div className="text-[15px] font-semibold text-white/85">
        {isFollowing ? "Nothing from Following yet." : "No posts yet."}
      </div>

      <div className="mt-2 text-sm text-white/55">
        {isFollowing ? (
          <>
            Following feed is coming soon. For now, you’ll see{" "}
            <span className="text-white/80 font-semibold">For You</span>.
          </>
        ) : (
          <>Tap explore to browse posts, or upload the first piece.</>
        )}
      </div>

      <div className="mt-5 flex items-center justify-center">
        <button
          type="button"
          onClick={onExplore}
          className="
            inline-flex h-11 items-center rounded-full
            border border-white/12 bg-white/[0.04]
            px-5 text-sm font-semibold text-white/90
            hover:bg-white/[0.07]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20
          "
        >
          Explore
        </button>
      </div>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div
      className="rounded-2xl sm:rounded-3xl border border-white/10 bg-black/25 backdrop-blur-xl overflow-hidden shadow-[0_22px_110px_rgba(0,0,0,0.55)]"
      style={{ maxHeight: "calc(100svh - 140px)" }}
    >
      <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/10" />
          <div className="space-y-2">
            <div className="h-3 w-28 sm:w-32 rounded bg-white/10" />
            <div className="h-3 w-16 sm:w-20 rounded bg-white/10" />
          </div>
        </div>
        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/10" />
      </div>

      <div className="h-[52svh] w-full bg-white/[0.06]" />

      <div className="px-4 sm:px-5 py-4 sm:py-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <div className="h-10 w-10 rounded-full bg-white/10 sm:h-10 sm:w-24 sm:rounded-full" />
            <div className="h-10 w-10 rounded-full bg-white/10 sm:h-10 sm:w-28 sm:rounded-full" />
            <div className="h-10 w-10 rounded-full bg-white/10 sm:h-10 sm:w-24 sm:rounded-full" />
          </div>
          <div className="h-10 w-10 rounded-full bg-white/10 sm:h-10 sm:w-24 sm:rounded-full" />
        </div>
        <div className="h-3 w-40 rounded bg-white/10" />
        <div className="h-3 w-full rounded bg-white/10" />
        <div className="h-3 w-2/3 rounded bg-white/10" />
        <div className="h-10 sm:h-11 w-full rounded-2xl bg-white/10" />
      </div>
    </div>
  );
}

// -------------------------
// Post Menu Dropdown
// -------------------------
const PostMenu = React.forwardRef(({ post, onAction }, ref) => {
  const menuItems = [
    {
      id: "save",
      label: post?.viewerState?.saved ? "Unsave" : "Save",
      icon: <Bookmark className="h-4 w-4" />,
    },
    {
      id: "copy-link",
      label: "Copy Link",
      icon: <LinkIcon className="h-4 w-4" />,
    },
    {
      id: "share",
      label: "Share",
      icon: <Share2 className="h-4 w-4" />,
    },
    {
      id: "report",
      label: "Report",
      icon: <Flag className="h-4 w-4" />,
      danger: true,
    },
  ];

  return (
    <div
      ref={ref}
      className="
        absolute top-16 right-4 z-50
        w-56
        rounded-2xl
        border border-white/12
        bg-black/90 backdrop-blur-2xl
        shadow-[0_24px_120px_rgba(0,0,0,0.85)]
        overflow-hidden
        animate-in fade-in slide-in-from-top-2 duration-200
      "
    >
      <div className="py-2">
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onAction(item.id)}
            className={`
              w-full px-4 py-3
              flex items-center gap-3
              text-left text-sm
              transition
              ${item.danger
                ? "text-rose-200/90 hover:bg-rose-500/20"
                : "text-white/85 hover:bg-white/10"
              }
              focus:outline-none
            `}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
});
